import { useCallback } from 'react';
import { useAgentSendMessageStream, useAgentConfirmMessageApi, ENTITY_CONFIGS } from '@hero/api-hooks';
import type { AgentPresetIntent } from '@hero/api';
import { useAgentChatStore } from './store';
import { bubbleFromBlocks } from './bubbleBlocks';
import { buildClientConfirmOutcome } from './buildBatchOutcome';
import { readPendingClientName } from './summarizePendingBatch';
import type { ConfirmationBatchResult, PendingConfirmation } from './types';

/**
 * Optional context the caller can attach to a `sendMessage` call.
 *
 * `presetIntent` lets the UI bypass the backend classifier on flows
 * where the domain is already known (preset chips, deep links from
 * entity drawers, future "ask about this order" affordances). Saves
 * ~300–800 ms of Haiku latency on the hot path.
 */
export interface SendMessageOptions {
  presetIntent?: AgentPresetIntent;
}

/**
 * Orchestrates the chat panel: turns user input into an SSE stream,
 * and turns confirmation clicks into the `confirmMessage` mutation.
 */
export const useAgentChatManager = () => {
  const stream = useAgentSendMessageStream();
  const confirmApi = useAgentConfirmMessageApi();
  const startUserTurn = useAgentChatStore(s => s.startUserTurn);
  const applyEvent = useAgentChatStore(s => s.applyEvent);
  const appendAssistantBubble = useAgentChatStore(s => s.appendAssistantBubble);
  const clearPendingConfirmation = useAgentChatStore(s => s.clearPendingConfirmation);
  const confirmationBatch = useAgentChatStore(s => s.confirmationBatch);
  const resolvingConfirmationId = useAgentChatStore(s => s.resolvingConfirmationId);

  const isResolvingConfirmation =
    !!confirmationBatch || !!resolvingConfirmationId || confirmApi.isPending;

  const readClientId = (pending: PendingConfirmation): string | undefined => {
    const id = pending.payload.clientId;
    return typeof id === 'string' ? id : undefined;
  };

  const executeConfirmPost = useCallback(
    async (
      pending: PendingConfirmation,
      conversationId: string,
      decision: 'confirm' | 'decline',
    ): Promise<ConfirmationBatchResult> => {
      const clientName = readPendingClientName(pending);
      const clientId = readClientId(pending);

      try {
        const result = await confirmApi.mutateAsync({
          conversationId,
          confirmationId: pending.id,
          decision,
          action: pending.action,
          payload: decision === 'confirm' ? pending.payload : undefined,
        });
        const blocks = result.blocks ?? [];
        const { text, widgets } = bubbleFromBlocks(blocks);
        return {
          confirmationId: pending.id,
          clientName,
          clientId,
          action: pending.action,
          ok: true,
          text,
          widgets,
        };
      } catch (err) {
        return {
          confirmationId: pending.id,
          clientName,
          clientId,
          action: pending.action,
          ok: false,
          widgets: [],
          errorMessage:
            err instanceof Error
              ? err.message
              : 'Failed to record your confirmation. Please try again.',
        };
      }
    },
    [confirmApi],
  );

  const appendClientOutcome = useCallback(
    (result: ConfirmationBatchResult) => {
      const { text, widgets, batchOutcomeCollapsed } = buildClientConfirmOutcome(result);
      appendAssistantBubble({
        id: `confirm-${result.confirmationId}`,
        role: 'assistant',
        text,
        widgets,
        batchOutcomeCollapsed,
      });
    },
    [appendAssistantBubble],
  );

  const sendMessage = useCallback(
    async (text: string, options?: SendMessageOptions) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      startUserTurn(trimmed);
      try {
        await stream.start(
          { text: trimmed, presetIntent: options?.presetIntent },
          event => {
            applyEvent(event);
          },
        );
      } catch (err) {
        applyEvent({
          type: 'error',
          runId: '',
          code: 'unknown',
          message:
            err instanceof Error
              ? err.message
              : 'Network error while streaming chat response.',
        });
      }
    },
    [stream, startUserTurn, applyEvent],
  );

  const respondToConfirmation = useCallback(
    async (decision: 'confirm' | 'decline', confirmationId: string) => {
      const state = useAgentChatStore.getState();
      if (state.confirmationBatch) return;

      const pending = state.pendingConfirmations.find(p => p.id === confirmationId);
      const conversationId = state.conversationId;
      if (!pending || !conversationId) return;

      const { setResolvingConfirmationId } = useAgentChatStore.getState();

      setResolvingConfirmationId(confirmationId);

      const batchResult = await executeConfirmPost(pending, conversationId, decision);
      clearPendingConfirmation(pending.id);
      setResolvingConfirmationId(undefined);

      if (decision === 'decline') {
        appendAssistantBubble({
          id: `decline-${pending.id}`,
          role: 'assistant',
          text: batchResult.text || "Got it — I'll skip that action.",
          widgets: batchResult.widgets,
        });
        return;
      }

      if (!batchResult.ok) {
        applyEvent({
          type: 'error',
          runId: '',
          code: 'unknown',
          message: batchResult.errorMessage ?? 'Failed to confirm.',
        });
        return;
      }

      if (batchResult.text || batchResult.widgets.length > 0) {
        appendClientOutcome(batchResult);
      }
    },
    [
      executeConfirmPost,
      applyEvent,
      appendAssistantBubble,
      appendClientOutcome,
      clearPendingConfirmation,
    ],
  );

  /**
   * Bulk-resolve every staged card in **parallel** (`Promise.all`).
   * Each client gets its own assistant row when its POST resolves:
   * Hero icon + green check + "Created 4 orders for Berain".
   */
  const bulkRespond = useCallback(
    async (decision: 'confirm' | 'decline') => {
      const state = useAgentChatStore.getState();
      if (state.confirmationBatch) return;

      const snapshot = [...state.pendingConfirmations];
      const conversationId = state.conversationId;
      if (snapshot.length === 0 || !conversationId) return;

      const isBulk = snapshot.length >= 2;
      const batchId = `batch-${Date.now()}`;

      const {
        startConfirmationBatch,
        advanceConfirmationBatch,
        finishConfirmationBatch,
      } = useAgentChatStore.getState();

      if (isBulk) {
        startConfirmationBatch({
          id: batchId,
          mode: decision,
          total: snapshot.length,
          progressBubbleId: '',
        });
      }

      let completedCount = 0;

      const runOne = async (pending: PendingConfirmation): Promise<ConfirmationBatchResult> => {
        const clientName = readPendingClientName(pending);

        if (isBulk) {
          advanceConfirmationBatch({
            completed: completedCount,
            currentConfirmationId: pending.id,
            currentClientName: clientName,
          });
        }

        const batchResult = await executeConfirmPost(pending, conversationId, decision);

        completedCount += 1;

        if (isBulk) {
          advanceConfirmationBatch({
            completed: completedCount,
            currentConfirmationId: undefined,
            currentClientName: undefined,
            result: batchResult,
          });
        }

        if (decision === 'confirm' && batchResult.ok && isBulk) {
          appendClientOutcome(batchResult);
        }

        return batchResult;
      };

      const results = await Promise.all(snapshot.map(runOne));

      clearPendingConfirmation();

      if (!isBulk) {
        const only = results[0];
        if (decision === 'decline') {
          appendAssistantBubble({
            id: `decline-${only.confirmationId}`,
            role: 'assistant',
            text: only.text || "Got it — I'll skip that action.",
            widgets: only.widgets,
          });
        } else if (only.ok && (only.text || only.widgets.length > 0)) {
          appendClientOutcome(only);
        } else if (!only.ok) {
          applyEvent({
            type: 'error',
            runId: '',
            code: 'unknown',
            message: only.errorMessage ?? 'Failed to confirm.',
          });
        }
      } else if (decision === 'decline') {
        appendAssistantBubble({
          id: `decline-batch-${batchId}`,
          role: 'assistant',
          text: "Got it — I'll skip those actions.",
          widgets: [],
        });
      } else {
        const failed = results.filter(r => !r.ok);
        if (failed.length > 0) {
          applyEvent({
            type: 'error',
            runId: '',
            code: 'unknown',
            message: `${failed.length} confirmation(s) failed.`,
          });
        }
      }

      finishConfirmationBatch();
    },
    [
      executeConfirmPost,
      appendAssistantBubble,
      appendClientOutcome,
      clearPendingConfirmation,
      applyEvent,
    ],
  );

  const confirmAll = useCallback(() => bulkRespond('confirm'), [bulkRespond]);
  const declineAll = useCallback(() => bulkRespond('decline'), [bulkRespond]);

  return {
    sendMessage,
    respondToConfirmation,
    confirmAll,
    declineAll,
    cancel: stream.stop,
    isConfirming: isResolvingConfirmation,
    isBatchConfirming: !!confirmationBatch,
    resolvingConfirmationId,
  };
};
